import { cmsFields, branchProps } from "./fields.js";
import {
  loadCms,
  saveCms,
  syncCmsToServer,
  isAdminAuthed,
  logoutAdmin,
  getDefaultBranches,
  persistMenuField,
  getMenuFieldValue,
  persistGalleryField,
  getGalleryFieldValue,
  persistHeroField,
  getHeroFieldValue,
  persistStoryParagraph,
  getStoryParagraphValue,
} from "./store.js";
import { applyGlobalPhone, persistBranchField } from "./apply.js";
import { resolveBranchImageSrc } from "./branch-images.js";
import { resolveImageSrc, isLegacyPicPath, isCustomImageSrc } from "./images.js";
import { MENU_FIELD_LABELS, MENU_EMPTY_PLACEHOLDERS } from "./menu-fields.js";
import { syncHomeSeoMeta } from "../seo.js";

const PAGE_MAP = {
  home: "home",
  menu: "menu",
  branches: "branches",
  contact: "contact",
  branch: "branch",
};

const EDIT_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#0c0b0a" stroke-width="2.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>`;

let activePopover = null;
let toolbarReady = false;

export function initInlineCms() {
  if (!isAdminAuthed() || document.body.dataset.page === "admin") return;

  document.body.classList.add("is-cms-editing");
  const seoSource = document.getElementById("seo-cms-description");
  if (seoSource) seoSource.hidden = false;
  if (!toolbarReady) {
    injectToolbar();
    toolbarReady = true;
  }
  markEditableFields();
}

export function refreshInlineCms() {
  if (!isAdminAuthed() || document.body.dataset.page === "admin") return;
  markEditableFields();
}

function getFieldsForPage() {
  const page = document.body.dataset.page;
  const cmsPage = PAGE_MAP[page] ?? "global";

  return cmsFields.filter(
    (field) =>
      field.page === "global" ||
      field.page === cmsPage ||
      field.page === "images"
  );
}

function markEditableFields() {
  getFieldsForPage().forEach((field) => {
    document.querySelectorAll(field.selector).forEach((el) => {
      attachEditControl(el, field);
    });
  });

  document.querySelectorAll("[data-cms-branch-prop]").forEach((el) => {
    const branchId =
      el.closest("[data-branch]")?.dataset.branch || document.body.dataset.branchId;
    if (!branchId) return;

    const prop = el.dataset.cmsBranchProp;
    const meta = branchProps.find((p) => p.prop === prop) ?? {
      prop,
      label: prop,
      type: el.dataset.cmsType || (el.tagName === "IMG" ? "image" : "text"),
    };

    attachEditControl(el, {
      isBranch: true,
      branchId,
      prop,
      label: meta.label,
      type: meta.type || (el.tagName === "IMG" ? "image" : "text"),
    });
  });

  markMenuFields();
  markGalleryFields();
  markHeroFields();
  markStoryFields();
}

function markGalleryFields() {
  document.querySelectorAll("[data-cms-gallery-field]").forEach((el) => {
    const item = el.closest("[data-cms-gallery-item]");
    if (!item) return;

    attachEditControl(el, {
      isGallery: true,
      index: Number(item.dataset.cmsGalleryIndex),
      fieldName: el.dataset.cmsGalleryField,
      label: el.dataset.cmsGalleryField === "src" ? "תמונת גלריה" : "טקסט חלופי",
      type: el.dataset.cmsGalleryField === "src" ? "image" : "text",
    });
  });
}

function markHeroFields() {
  document.querySelectorAll("[data-cms-hero-field]").forEach((el) => {
    const item = el.closest("[data-cms-hero-item]");
    if (!item) return;

    attachEditControl(el, {
      isHero: true,
      index: Number(item.dataset.cmsHeroIndex),
      fieldName: el.dataset.cmsHeroField,
      label: el.dataset.cmsHeroField === "src" ? "שקופית קרוסלה" : "טקסט חלופי",
      type: el.dataset.cmsHeroField === "src" ? "image" : "text",
    });
  });
}

function markStoryFields() {
  document.querySelectorAll("[data-cms-story-field]").forEach((el) => {
    const item = el.closest("[data-cms-story-item]");
    if (!item) return;

    attachEditControl(el, {
      isStory: true,
      index: Number(item.dataset.cmsStoryIndex),
      label: "פסקה",
      type: "text",
    });
  });
}

function markMenuFields() {
  document.querySelectorAll("[data-cms-menu-section-title]").forEach((el) => {
    attachEditControl(el, {
      isMenu: true,
      sectionId: el.dataset.cmsMenuSectionTitle,
      isSectionTitle: true,
      label: "כותרת קטגוריה",
      type: "text",
    });
  });

  document.querySelectorAll("[data-cms-menu-field]").forEach((el) => {
    const item = el.closest("[data-cms-menu-item]");
    if (!item) return;

    attachEditControl(el, {
      isMenu: true,
      sectionId: item.dataset.cmsMenuSection,
      itemIndex: Number(item.dataset.cmsMenuIndex),
      fieldName: el.dataset.cmsMenuField,
      label: MENU_FIELD_LABELS[el.dataset.cmsMenuField] ?? el.dataset.cmsMenuField,
      type: "text",
    });
  });
}

function attachEditControl(el, field) {
  if (el.dataset.cmsReady === "1") return;

  let host = el;
  if (el.tagName === "IMG") {
    unwrapBrokenImageWrap(el);
    el.dataset.cmsReady = "1";
    host = getImageHost(el);
  }

  if (host.dataset.cmsReady === "1") return;
  host.dataset.cmsReady = "1";
  host.classList.add("bm-cms-editable");

  if (getComputedStyle(host).position === "static") {
    host.style.position = "relative";
  }

  if (host.querySelector(":scope > .bm-cms-edit-btn")) return;

  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "bm-cms-edit-btn";
  btn.setAttribute("aria-label", `ערוך ${field.label}`);
  btn.innerHTML = EDIT_ICON;
  btn.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    openEditor(el, field);
  });
  host.appendChild(btn);
}

function unwrapBrokenImageWrap(img) {
  const wrap = img.parentElement;
  if (!wrap?.classList.contains("bm-cms-editable-wrap")) return;
  wrap.parentElement?.insertBefore(img, wrap);
  wrap.remove();
}

function getImageHost(img) {
  const host =
    img.closest(".img-container") ??
    img.closest(".bm-page-hero") ??
    img.closest(".bm-nav__logo-link") ??
    img.closest(".bm-nav a") ??
    img.closest(".bm-footer__logo") ??
    img.closest(".carousel-item") ??
    img.parentElement;

  return host ?? img;
}

function readFieldValue(el, field) {
  if (field.isGallery) {
    const stored = getGalleryFieldValue(field.index, field.fieldName);
    if (field.type === "image") return el.getAttribute("src") ?? stored ?? "";
    return stored || el.textContent.trim();
  }

  if (field.isHero) {
    const stored = getHeroFieldValue(field.index, field.fieldName);
    if (field.type === "image") return el.getAttribute("src") ?? stored ?? "";
    return stored || el.getAttribute("alt")?.trim() || "";
  }

  if (field.isStory) {
    const stored = getStoryParagraphValue(field.index);
    return stored || el.textContent.trim();
  }

  if (field.isMenu) {
    if (el.classList.contains("is-empty")) return "";
    const stored = getMenuFieldValue({
      sectionId: field.sectionId,
      itemIndex: field.itemIndex,
      fieldName: field.fieldName,
      isSectionTitle: field.isSectionTitle,
    });
    if (stored) return stored;
    return field.isSectionTitle ? el.textContent.trim() : el.textContent.trim();
  }

  if (field.isBranch) {
    const cms = loadCms();
    const branches = cms.branches ?? getDefaultBranches();
    const stored = branches[field.branchId]?.[field.prop];
    if (field.type === "image") return el.getAttribute("src") ?? stored ?? "";
    if (stored) return stored;
    return el.textContent.trim();
  }

  const cms = loadCms();
  if (field.type === "link") {
    return cms.links?.[field.key] ?? el.getAttribute("href") ?? "";
  }
  if (field.type === "attr" && field.attr) {
    return cms.text?.[field.key] ?? el.getAttribute(field.attr) ?? "";
  }
  if (field.type === "image") {
    const stored = cms.images?.[field.key];
    if (stored && isCustomImageSrc(stored)) return stored;
    return el.getAttribute("src") ?? stored ?? "";
  }

  const stored = cms.text?.[field.key];
  if (stored) return stored;

  const clone = el.cloneNode(true);
  clone.querySelector(".bm-cms-edit-btn")?.remove();
  return field.type === "html" ? clone.innerHTML : clone.textContent.trim();
}

function openEditor(el, field) {
  closePopover();

  const current = readFieldValue(el, field);
  const type = field.type ?? "text";

  const backdrop = document.createElement("div");
  backdrop.className = "bm-cms-popover-backdrop";

  const popover = document.createElement("div");
  popover.className = "bm-cms-popover";
  popover.setAttribute("role", "dialog");
  popover.setAttribute("aria-label", field.label);

  const title = document.createElement("h2");
  title.className = "bm-cms-popover__title";
  title.textContent = field.label;

  let input;
  if (type === "image") {
    input = buildImageEditor(current);
  } else if (type === "link") {
    input = document.createElement("input");
    input.type = "url";
    input.className = "bm-form-control bm-cms-popover__input";
    input.value = current;
  } else if (type === "attr") {
    input = document.createElement("textarea");
    input.className = "bm-form-control bm-cms-popover__input";
    input.rows = 2;
    input.value = current;
  } else if (type === "html") {
    input = document.createElement("textarea");
    input.className = "bm-form-control bm-cms-popover__input";
    input.rows = 4;
    input.value = current;
  } else {
    input = document.createElement("textarea");
    input.className = "bm-form-control bm-cms-popover__input";
    input.rows = 3;
    input.value = current;
  }

  const actions = document.createElement("div");
  actions.className = "bm-cms-popover__actions";

  const cancelBtn = document.createElement("button");
  cancelBtn.type = "button";
  cancelBtn.className = "bm-btn bm-btn--ghost";
  cancelBtn.textContent = "ביטול";

  const saveBtn = document.createElement("button");
  saveBtn.type = "button";
  saveBtn.className = "bm-btn bm-btn--primary";
  saveBtn.textContent = "שמירה";

  actions.append(cancelBtn, saveBtn);
  popover.append(title, input, actions);
  document.body.append(backdrop, popover);
  activePopover = { backdrop, popover };

  const close = () => closePopover();
  backdrop.addEventListener("click", close);
  cancelBtn.addEventListener("click", close);

  saveBtn.addEventListener("click", () => {
    const value =
      type === "image"
        ? popover.querySelector(".bm-cms-popover__url").value.trim()
        : input.value.trim();

    if (!value) return;

    persistField(field, value);
    applySavedField(el, field, value);
    if (document.body.dataset.page === "home") {
      syncHomeSeoMeta();
    }
    showToast("נשמר");
    close();
  });

  (type === "image" ? popover.querySelector(".bm-cms-popover__url") : input)?.focus();
}

function buildImageEditor(current) {
  const input = document.createElement("div");
  input.className = "bm-cms-popover__image";
  input.innerHTML = `
    <img class="bm-cms-popover__preview" src="${escapeAttr(current)}" alt="">
    <label class="bm-form-label">קישור לתמונה</label>
    <input type="text" class="bm-form-control bm-cms-popover__url" value="${escapeAttr(current)}">
    <label class="bm-form-label">או העלאה מהמחשב</label>
    <input type="file" accept="image/*" class="bm-cms-popover__file bm-admin-upload">
  `;
  const fileInput = input.querySelector(".bm-cms-popover__file");
  const urlInput = input.querySelector(".bm-cms-popover__url");
  const preview = input.querySelector(".bm-cms-popover__preview");
  fileInput.addEventListener("change", () => {
    const file = fileInput.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      urlInput.value = reader.result;
      preview.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
  urlInput.addEventListener("input", () => {
    preview.src = urlInput.value;
  });
  return input;
}

function closePopover() {
  activePopover?.backdrop.remove();
  activePopover?.popover.remove();
  activePopover = null;
}

function persistField(field, value) {
  if (field.isGallery) {
    persistGalleryField(field.index, field.fieldName, value);
    return;
  }

  if (field.isHero) {
    persistHeroField(field.index, field.fieldName, value);
    return;
  }

  if (field.isStory) {
    persistStoryParagraph(field.index, value);
    return;
  }

  if (field.isMenu) {
    persistMenuField({
      sectionId: field.sectionId,
      itemIndex: field.itemIndex,
      fieldName: field.fieldName,
      value,
      isSectionTitle: field.isSectionTitle,
    });
    return;
  }

  if (field.isBranch) {
    persistBranchField(field.branchId, field.prop, value);
    return;
  }

  const cms = loadCms();
  if (field.type === "link") {
    cms.links = cms.links ?? {};
    cms.links[field.key] = value;
  } else if (field.type === "image") {
    cms.images = cms.images ?? {};
    if (isCustomImageSrc(value)) {
      cms.images[field.key] = value;
    } else if (isLegacyPicPath(value)) {
      delete cms.images[field.key];
    } else {
      cms.images[field.key] = value;
    }
  } else {
    cms.text = cms.text ?? {};
    cms.text[field.key] = value;
  }
  saveCms(cms);
}

function applySavedField(el, field, value) {
  if (field.isGallery || field.isHero) {
    if (field.type === "image") {
      const resolved = resolveImageSrc(value, el);
      if (resolved) el.setAttribute("src", resolved);
      if (field.fieldName === "src") el.setAttribute("alt", el.getAttribute("alt") || "תמונה");
    } else if (field.fieldName === "alt") {
      el.setAttribute("alt", value);
    } else {
      updateElement(el, "text", value);
    }
    return;
  }

  if (field.isStory) {
    updateElement(el, "text", value);
    return;
  }

  if (field.isMenu) {
    if (field.fieldName === "sizes" || field.fieldName === "desc") {
      const hasValue = Boolean(value);
      el.classList.toggle("is-empty", !hasValue);
      el.textContent = hasValue ? value : MENU_EMPTY_PLACEHOLDERS[field.fieldName];
      reattachEditButton(el);
    } else {
      updateElement(el, "text", value);
    }
    if (field.isSectionTitle) {
      const navLink = document.querySelector(
        `.bm-menu-nav__link[href="#${field.sectionId}"]`
      );
      if (navLink) navLink.textContent = value;
    }
    return;
  }

  if (field.isBranch) {
    if (field.type === "image") {
      el.setAttribute("src", resolveBranchImageSrc(field.branchId, value));
    } else {
      updateElement(el, "text", value);
    }
    return;
  }

  if (field.selector) {
    document.querySelectorAll(field.selector).forEach((node) => {
      applyValueToNode(node, field.type, value, field.attr);
    });
  } else {
    applyValueToNode(el, field.type, value, field.attr);
  }

  if (field.key === "global.phone") {
    applyGlobalPhone(value);
  }
}

function applyValueToNode(el, type, value, attr) {
  if (type === "link") {
    el.setAttribute("href", value);
    return;
  }
  if (type === "attr" && attr) {
    el.setAttribute(attr, value);
    return;
  }
  updateElement(el, type, value);
}

function updateElement(el, type, value) {
  const btn =
    el.querySelector(".bm-cms-edit-btn") ??
    el.parentElement?.querySelector(":scope > .bm-cms-edit-btn");

  if (type === "image") {
    const resolved = resolveImageSrc(value, el);
    if (resolved) el.setAttribute("src", resolved);
    return;
  }
  if (type === "html") {
    el.innerHTML = value;
  } else {
    el.textContent = value;
  }
  reattachEditButton(el, btn);
}

function reattachEditButton(el, btn) {
  const editBtn =
    btn ??
    el.querySelector(".bm-cms-edit-btn") ??
    el.parentElement?.querySelector(":scope > .bm-cms-edit-btn");
  if (editBtn) (editBtn.parentElement ?? el).appendChild(editBtn);
}

function injectToolbar() {
  if (document.getElementById("bm-cms-toolbar")) return;

  const bar = document.createElement("div");
  bar.id = "bm-cms-toolbar";
  bar.className = "bm-cms-toolbar";
  bar.innerHTML = `
    <button type="button" class="bm-btn bm-btn--primary bm-btn--sm" data-cms-action="save">שמירה</button>
    <button type="button" class="bm-btn bm-btn--ghost bm-btn--sm" data-cms-action="reset">איפוס</button>
    <button type="button" class="bm-btn bm-btn--ghost bm-btn--sm" data-cms-action="logout">יציאה</button>
  `;

  bar.querySelector('[data-cms-action="save"]').addEventListener("click", async () => {
    const saveBtn = bar.querySelector('[data-cms-action="save"]');
    saveBtn.disabled = true;
    try {
      saveCms(loadCms(), { syncToServer: false });
      await syncCmsToServer();
      if (document.body.dataset.page === "home") {
        syncHomeSeoMeta();
      }
      showToast("נשמר בהצלחה — כל המבקרים יראו את השינויים");
    } catch {
      showToast("השמירה המקומית הצליחה, אבל העלאה לשרת נכשלה. נסו שוב.", "error");
    } finally {
      saveBtn.disabled = false;
    }
  });
  bar.querySelector('[data-cms-action="reset"]').addEventListener("click", async () => {
    if (!confirm("לאפס את כל השינויים?")) return;
    localStorage.removeItem("burgermunch-cms");
    try {
      await syncCmsToServer();
    } catch {
      showToast("האיפוס המקומי הצליח, אבל העלאה לשרת נכשלה.", "error");
      window.location.reload();
      return;
    }
    window.location.reload();
  });
  bar.querySelector('[data-cms-action="logout"]').addEventListener("click", async () => {
    try {
      await logoutAdmin();
      window.location.href = "index.html";
    } catch { window.alert("ההתנתקות נכשלה. נסו שוב."); }
  });

  document.body.appendChild(bar);
}

function showToast(msg, type = "success") {
  const toast = document.createElement("div");
  toast.className = `bm-cms-toast is-${type}`;
  toast.textContent = msg;
  document.body.appendChild(toast);
  requestAnimationFrame(() => toast.classList.add("is-visible"));
  setTimeout(() => {
    toast.classList.remove("is-visible");
    setTimeout(() => toast.remove(), 300);
  }, 2200);
}

export function showCmsToast(msg, type = "success") {
  showToast(msg, type);
}

function escapeAttr(str) {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;");
}
